<template>
    <div>
        <HamletTable
            ref="hamletTable"
            :columns="columns"
            :fetch="fetch"
            :search="true"
            table-container-class="table-responsive hamlet-table scheduler-tasks-table"
            :default-per-page="savedPerPage"
            :page-options="pageOptions"
            :default-filters="filters"
            @onRowClick="onRowClick"
            @per-page-change="onPerPageChange"
            @page-change="onPageChange"
        >
            <template #header-label>
                Scheduler Tasks
            </template>

            <template #cell-status="data">
                <b-badge :variant="statusVariant(data.item.status)">
                    {{ data.item.status }}
                </b-badge>
            </template>

            <template #cell-nextExecutionTime="data">
                {{ formatDateTime(data.item.nextExecutionTime) }}
            </template>

            <template #cell-plannedAt="data">
                {{ formatDateTime(data.item.plannedAt) }}
            </template>

            <template #filter-cell-id>
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.id"
                    @search="filterById"
                />
            </template>

            <template #filter-cell-title>
                <FilterInput
                    style="min-width: 180px;"
                    :initial-value="filters.title"
                    @search="filterByTitle"
                />
            </template>

            <template #filter-cell-status>
                <VueSelectFilter
                    style="min-width: 140px;"
                    :options="statusOptions"
                    :initial-value="selectedStatusOption"
                    @filter="filterByStatus"
                />
            </template>
        </HamletTable>
    </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import { HamletTable } from '@core/components/common'
import { FilterInput, VueSelectFilter } from '@core/components/common/filter'
import { SystemApi } from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import dateRenderer from '@core/mixins/date-renderer'

const DEFAULT_PER_PAGE = 20

function cloneFilters(filters) {
    return JSON.parse(JSON.stringify(filters || {}))
}

function normalizeSettings(settings) {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {
            filters: {},
            perPage: DEFAULT_PER_PAGE,
            page: 1,
        }
    }

    return {
        filters: cloneFilters(settings.filters),
        perPage: Number(settings.perPage) || DEFAULT_PER_PAGE,
        page: Number(settings.page) || 1,
    }
}

export default {
    components: {
        BBadge,
        HamletTable,
        FilterInput,
        VueSelectFilter,
    },
    mixins: [backendResponse, dateRenderer],
    data() {
        const savedSettings = normalizeSettings(this.$store.getters.runtimeConfig.schedulerTasksSettings)

        return {
            filters: savedSettings.filters,
            savedPerPage: savedSettings.perPage,
            savedPage: savedSettings.page,
            columns: [
                { key: 'id', label: 'ID' },
                { key: 'title', label: 'Title' },
                { key: 'status', label: 'Status' },
                { key: 'taskType', label: 'Task Type' },
                { key: 'nextExecutionTime', label: 'Next Execution Time' },
                { key: 'plannedAt', label: 'Planned At' },
                { key: 'executedTimes', label: 'Executed Times' },
                { key: 'terminalFailures', label: 'Terminal Failures' },
                { key: 'subscriberId', label: 'Subscriber ID' },
            ],
            statusOptions: [
                { status: 'planned', title: 'planned' },
                { status: 'running', title: 'running' },
                { status: 'executed', title: 'executed' },
                { status: 'failed', title: 'failed' },
            ],
            pageOptions: [20, 30, 50, 100],
        }
    },
    computed: {
        selectedStatusOption() {
            if (!this.filters.status) {
                return []
            }

            return this.statusOptions.filter(option => option.status === this.filters.status)
        },
    },
    methods: {
        formatDateTime(value) {
            return value ? this.renderDateTime(value) : '-'
        },
        statusVariant(status) {
            const map = {
                planned: 'secondary',
                running: 'info',
                executed: 'success',
                failed: 'danger',
            }

            return map[status] || 'secondary'
        },
        saveSettings() {
            this.$store.dispatch('setRuntimeProperty', {
                name: 'schedulerTasksSettings',
                value: {
                    filters: this.filters,
                    perPage: this.savedPerPage,
                    page: this.savedPage,
                },
                removeOnOrganizationSwitch: true,
            })
        },
        onPerPageChange(perPage) {
            this.savedPerPage = perPage
            this.saveSettings()
        },
        onPageChange(page) {
            this.savedPage = page
            this.saveSettings()
        },
        setFilterValue(key, value) {
            if (value === null || value === undefined || value === '') {
                if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
                    delete this.filters[key]
                }
                return
            }

            this.filters[key] = value
        },
        filterByStatus(items) {
            const status = items.length > 0 ? items[0].status : null
            this.setFilterValue('status', status)
            this.$refs.hamletTable.filter('status', status)
            this.saveSettings()
        },
        filterByTitle(value) {
            this.setFilterValue('title', value)
            this.$refs.hamletTable.filter('title', value)
            this.saveSettings()
        },
        filterById(value) {
            this.setFilterValue('id', value)
            this.$refs.hamletTable.filter('id', value)
            this.saveSettings()
        },
        onRowClick(item) {
            if (!this.$can('visit', 'system-scheduler-task-card')) {
                return
            }

            this.$router.push({ name: 'system-scheduler-task-card', params: { id: item.id } })
        },
        fetch(config, cb) {
            this.savedPage = config.page
            this.savedPerPage = config.perPage
            this.saveSettings()

            return SystemApi.scheduler.getTasks(config.filters, config.page, config.perPage)
                .then(r => {
                    const items = (r.data.items || []).map(task => ({
                        ...task,
                        title: task.metadata && task.metadata.title ? task.metadata.title : '-',
                        subscriberId: task.metadata && task.metadata.subscriberId ? task.metadata.subscriberId : '-',
                    }))

                    cb(r.data.total || 0, items)
                })
                .catch(this.handleBackendError)
        },
    },
}
</script>
